import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { monthsLowerCase } from "../../consts/forecast";
import { formatAmount } from "../../helpers/quickbooks";
import { LoaderSpinner } from "../LoaderSpinner";
import { useState } from "react";

export const MonthlySummaryForecast = ({
    selectedChannel,
    forecastType,
    forecastMethod,
    summaryDataByYear,
    summaryDataByMonth,
    monthMethods
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const getData = () => {
        if (forecastType === "year") {
            if (forecastMethod === "ad-spend") return summaryDataByYear?.["ad_spend"];
            if (forecastMethod === "revenue") return summaryDataByYear?.["revenue_target"];
        }
        if (forecastType === "month") {
            const normalizeType = (type) => {
                if (type === "ad-spend") return "ad_spend";
                if (type === "revenue") return "revenue_target";
                return type;
            };
            const result = {};
            Object.entries(monthMethods).forEach(([month, type]) => {
                const normalizedType = normalizeType(type);
                const section = summaryDataByMonth?.[normalizedType];

                if (!section) return;

                Object.entries(section).forEach(([metric, monthValues]) => {
                    if (!result[metric]) {
                        result[metric] = {};
                    }
                    result[metric][month] = monthValues[month];
                });
            });
            return result;
        }
        return null;
    }
    const getMonthsData = (data) => {
        return monthsLowerCase
            .filter((month) => data.hasOwnProperty(month))
            .map((month) => [month, data[month]]);
    };

    const handleExportClick = () => {
        try {
            setIsExporting(true);
            const data = getData();
            if (!data) {
                throw new Error('No data available to export');
            }

            // Convert data to CSV
            const headers = ['Metric', ...months];
            const rows = [];

            // Add AOV row
            if (data.aov) {
                rows.push(['AOV', ...getMonthsData(data.aov).map(([_, value]) => value)]);
            }

            // Add Ad Spend row
            if (data.ad_spend) {
                rows.push(['Ad Spend', ...getMonthsData(data.ad_spend).map(([_, value]) => value)]);
            }

            // Add MER row
            if (data.mer) {
                rows.push(['MER', ...getMonthsData(data.mer).map(([_, value]) => value)]);
            }

            // Add Total Revenue row
            if (data.total_revenue) {
                rows.push(['Total Revenue', ...getMonthsData(data.total_revenue).map(([_, value]) => value)]);
            }

            // Add Total Units row
            if (data.total_units) {
                rows.push(['Total Units', ...getMonthsData(data.total_units).map(([_, value]) => value)]);
            }

            // Add channel-specific rows
            if ((selectedChannel === "shopify_channel" || selectedChannel === "all_channels") && data.shopify) {
                rows.push(['Shopify', ...getMonthsData(data.shopify).map(([_, value]) => value)]);
            }
            if ((selectedChannel === "amazon_channel" || selectedChannel === "all_channels") && data.amazon) {
                rows.push(['Amazon', ...getMonthsData(data.amazon).map(([_, value]) => value)]);
            }
            if ((selectedChannel === "wholesale_channel" || selectedChannel === "all_channels") && data.wholesale) {
                rows.push(['Wholesale', ...getMonthsData(data.wholesale).map(([_, value]) => value)]);
            }

            // Convert to CSV string
            const csvContent = [
                headers.join(','),
                ...rows.map(row => row.join(','))
            ].join('\n');

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Generate filename with timestamp
            const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:]/g, '').replace('T', '_');
            const filename = `monthly_summary_forecast_${timestamp}.csv`;
            
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Export error:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsExporting(false);
        }
    };

    const data = getData();
    const buttonText = isExporting ? 'Exporting...' : 'Export CSV';
    return (
        <div className="rounded-lg border bg-card text-card-foreground shadow-2xs">
            <div className="flex flex-col space-y-1.5 p-6 pb-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-semibold leading-none tracking-tight">
                        Monthly Summary
                    </h3>
                    <button
                        title="Export data as CSV"
                        onClick={handleExportClick}
                        className="inline-flex items-center px-2 py-1 text-sm font-medium rounded-md transition-colors duration-200 text-gray-700 bg-white border border-primary hover:bg-gray-100"
                        disabled={isExporting || !data}
                    >
                        {isExporting ? (
                            <div className="mr-2">
                                <LoaderSpinner width="12" height="12" border="2" parentHeight="h-4" colorSpinner="black" />
                            </div>
                        ) : (
                            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        )}
                        {buttonText}
                    </button>
                </div>
            </div>
            <div className="p-6 pt-0">
                <div className="overflow-x-auto">
                    <div className="relative w-full overflow-auto">
                        <table className="w-full caption-bottom text-sm min-w-full">
                            <thead>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <th className="h-12 px-4 align-middle text-left font-medium w-48 bg-[rgba(239,246,255,1)] text-[rgba(29,58,138,1)] w-64">
                                        METRIC
                                    </th>
                                    {months.map((month) => (
                                        <th key={month} className="h-12 px-4 align-middle text-center font-medium text-gray-600 min-w-32 bg-[rgba(239,246,255,1)] text-[rgba(29,58,138,1)]">
                                            {month}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                        AOV
                                    </td>
                                    {data?.aov && Object.keys(data.aov).length > 0 && (
                                        getMonthsData(data.aov).map(([month, value]) => (
                                            <td key={month} className="p-4 align-middle text-center">
                                                {formatAmount(value)}
                                            </td>
                                        ))
                                    )}
                                </tr>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <td className="p-4 align-middle font-bold bg-[rgba(239,246,255,1)] text-[rgba(29,58,138,1)] w-64 whitespace-nowrap">
                                        Ad Spend
                                    </td>
                                    {data?.ad_spend && Object.keys(data.ad_spend).length > 0 && (
                                        getMonthsData(data.ad_spend).map(([month, value]) => (
                                            <td key={month} className="p-4 align-middle text-center bg-[rgba(239,246,255,1)] text-[rgba(29,58,138,1)]">
                                                {formatAmount(value)}
                                            </td>
                                        ))
                                    )}
                                </tr>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                        MER
                                    </td>
                                    {data?.mer && Object.keys(data.mer).length > 0 && (
                                        getMonthsData(data.mer).map(([month, value]) => (
                                            <td key={month} className="p-4 align-middle text-center">
                                                {formatAmount(value, true)}
                                            </td>
                                        ))
                                    )}
                                </tr>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <td className="p-4 align-middle font-bold text-[rgba(29,58,138,1)] bg-[rgba(239,246,255,1)] w-64 whitespace-nowrap">
                                        Total Revenue
                                    </td>
                                    {data?.total_revenue && Object.keys(data.total_revenue).length > 0 && (
                                        getMonthsData(data.total_revenue).map(([month, value]) => (
                                            <td key={month} className="p-4 align-middle text-center bg-[rgba(239,246,255,1)] text-[rgba(29,58,138,1)]">
                                                {formatAmount(value)}
                                            </td>
                                        ))
                                    )}
                                </tr>
                                <tr className="transition-colors hover:bg-gray-100 border-b">
                                    <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                        Total Units
                                    </td>
                                    {data?.total_units && Object.keys(data.total_units).length > 0 && (
                                        getMonthsData(data.total_units).map(([month, value]) => (
                                            <td key={month} className="p-4 align-middle text-center">
                                                {formatAmount(value, true)}
                                            </td>
                                        ))
                                    )}
                                </tr>
                                {((selectedChannel === "shopify_channel") || (selectedChannel === "all_channels")) && (
                                    <tr className="transition-colors hover:bg-gray-100 border-b">
                                        <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                            Shopify
                                        </td>
                                        {data?.shopify && Object.keys(data.shopify).length > 0 && (
                                            getMonthsData(data.shopify).map(([month, value]) => (
                                                <td key={month} className="p-4 align-middle text-center">
                                                    {formatAmount(value)}
                                                </td>
                                            ))
                                        )}
                                    </tr>
                                )}
                                {((selectedChannel === "amazon_channel") || (selectedChannel === "all_channels")) && (
                                    <tr className="transition-colors hover:bg-gray-100 border-b">
                                        <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                            Amazon
                                        </td>
                                        {data?.amazon && Object.keys(data.amazon).length > 0 && (
                                            getMonthsData(data.amazon).map(([month, value]) => (
                                                <td key={month} className="p-4 align-middle text-center">
                                                    {formatAmount(value)}
                                                </td>
                                            ))
                                        )}
                                    </tr>
                                )}
                                {((selectedChannel === "wholesale_channel") || (selectedChannel === "all_channels")) && (
                                    <tr className="transition-colors hover:bg-gray-100 border-b">
                                        <td className="p-4 align-middle font-bold text-gray-900 w-64 whitespace-nowrap">
                                            Wholesale
                                        </td>
                                        {data?.wholesale && Object.keys(data.wholesale).length > 0 && (
                                            getMonthsData(data.wholesale).map(([month, value]) => (
                                                <td key={month} className="p-4 align-middle text-center">
                                                    {formatAmount(value)}
                                                </td>
                                            ))
                                        )}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div> 
    );
}